import React from "react";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";

const { $Divider, $AmountLabel } = Common.Components;

/**
 * @description Displays a summary of the imported file results in the FileConverter component.
 * @author Ishan Udyoga <ishanud@unicorn-solutions.com>
 * @link Task - https://unicorn-solutions.atlassian.net/browse/UBX4-25356
 * @since 2025-06-19
 */
const ImportResult: React.FC<PropsFromRedux> = (props) => {
  const { t } = useTranslation();

  const { importResult } = props;

  const {
    ReceiptNumber,
    NoOfInvoices,
    TotalValueOfInvoices,
    NoOfCreditNotes,
    CreditNoteSum,
    ErrorList
  } = importResult ?? {};

  // Component to display the error list
  const ErrorListContent = () => {
    return (
      <div className="error-list-container">
        <ul className="error-list">
          {ErrorList?.map((error: string, index: number) => (
            <li key={index}>{error}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <>
      <div className="px-3 pb-2">
        <div data-testid="preview-summary">
          <div className="d-flex flex-column">
            <h2 className="mb-4">
              {t(
                "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.FILE_IMPORT_RESULT"
              )}
            </h2>
            {ErrorList?.length == 0 && (
              <>
                <div className="mt-2">
                  <div className="d-flex justify-content-between">
                    <span>
                      {t(
                        "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.RECEIPT_NUMBER"
                      )}
                    </span>
                    <span
                      data-testid="ReceiptNumber"
                      className="font-weight-bold text-right"
                    >
                      {ReceiptNumber || "-"}
                    </span>
                  </div>
                </div>
                <$Divider className="my-3" />
                <div className="d-flex justify-content-between">
                  <span>
                    {t(
                      "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.NO_OF_INVOICES"
                    )}
                  </span>
                  <span
                    data-testid="NoOfInvoices"
                    className="font-weight-bold text-right"
                  >
                    {NoOfInvoices || "-"}
                  </span>
                </div>
                <$Divider className="my-3" />
                <div className="d-flex justify-content-between">
                  <span>
                    {t(
                      "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.NO_OF_CREDIT_NOTES"
                    )}
                  </span>
                  <span
                    data-testid="NoOfCreditNotes"
                    className="font-weight-bold text-right"
                  >
                    {NoOfCreditNotes || "-"}
                  </span>
                </div>
                <$Divider className="my-3" />
                <div className="d-flex justify-content-between">
                  <span>
                    {t(
                      "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.CREDIT_NOTE_SUM"
                    )}
                  </span>
                  <span
                    data-testid="CreditNoteSum"
                    className="font-weight-bold text-right"
                  >
                    <$AmountLabel value={CreditNoteSum} />
                  </span>
                </div>
                <$Divider className="my-3" />
                <div className="d-flex justify-content-between">
                  <span>
                    {t(
                      "US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.TOTAL_VALUE_OF_INVOICES"
                    )}
                  </span>
                  <span
                    data-testid="totalValueOfInvoices"
                    className="font-weight-bold text-right"
                  >
                    <$AmountLabel value={TotalValueOfInvoices} />
                  </span>
                </div>
              </>
            )}
            {/* Display error list if it exists */}
            {ErrorList?.length > 0 && (
              <>
                <div className="mb-3">
                  <h4 className="text-danger">
                    {t("US.COLLECTION.INPUT-OUTPUT:FILE_CONVERTER.ERROR_LIST")}
                  </h4>
                  <ErrorListContent />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { fileConverter } = state;

  const { importFiles, fileList } = fileConverter;

  return {
    fileList,
    importResult: importFiles.data,
  };
};

const connector = connect(mapStateToProps, {});

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ImportResult);
